import { Button, lightPalette, onlyMobile } from "@ameelio/ui";
import {
  Alert,
  alpha,
  Box,
  Grid,
  useMediaQuery as measureScreenWidth,
  Stack,
  Typography,
} from "@mui/material";
import { common, grey, red } from "@mui/material/colors";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { track } from "../analytics";
import {
  Call,
  CallCallStatus,
  CallFeature,
  CallParticipant,
  CallType,
  ParticipantRole,
} from "../api/graphql";
import addStylesForDevices from "../lib/addStylesForDevices";
import { belowLaptop, belowLargeTablet } from "../lib/responsiveHelpers";
import {
  callIsWebinar,
  isConfidentialCall,
  securityDisclaimerCall,
} from "../lib/securityFeaturesUtils.ts";
import { formatTimeRange } from "../lib/timeFormats";
import useLocalVideo from "../lib/useLocalVideo";
import useNow from "../lib/useNow";
import useOnLoad from "../lib/useOnLoad";
import videoStreamDimensions from "../lib/videoStreamDimensions.ts";
import { darkPalette, ltrTheme } from "../theme";
import VideoParticipant from "./LiveCall/VideoParticipant";
import MediaControls from "./MediaControls";
import MeetingParticipantsAccordion from "./MeetingParticipantsAccordion";
import WebinarInfoCard from "./WebinarInfoCard";
import { sendToParent } from "./iframeMessaging.ts";

type Props = {
  currentParticipant: Pick<
    CallParticipant,
    "externalId" | "type" | "role" | "fullName"
  >;
  call: Pick<
    Call,
    "id" | "features" | "callType" | "callStatus" | "startedAt" | "endedAt"
  > & {
    participants: Pick<
      CallParticipant,
      "externalId" | "fullName" | "role" | "type"
    >[];
  };
  fromPath: string;
  standalone?: boolean;
  title?: string | null;
  onJoinButtonClick?: () => void;
  beginMuted: boolean;
  beginHidden: boolean;
  onToggleAudio: () => void;
  onToggleVideo: () => void;
};

type LaptopDesktopScreenProps = {
  videoPreview: React.ReactNode;
  meetingInfo: React.ReactNode;
  controls: React.ReactNode;
};

type MobileTabletScreenProps = Omit<LaptopDesktopScreenProps, "controls"> & {
  stream?: MediaStream;
};

function MobileTabletLobby({
  videoPreview,
  meetingInfo,
  stream,
}: MobileTabletScreenProps) {
  const isMobile = onlyMobile(measureScreenWidth);
  // determine the size of the floating window
  const myVideoDims = (() => {
    // selfie shrinks to the top left corner
    const { aspectRatio } = videoStreamDimensions(stream);
    const videoScaleWidth = aspectRatio >= 1 ? 20 : isMobile ? 5 : 12;
    const spacing = aspectRatio >= 1 ? 25 : isMobile ? 10 : 17;
    return {
      width: `max(${videoScaleWidth}%, ${ltrTheme.spacing(spacing)})`,
      paddingBottom: `max(${videoScaleWidth / aspectRatio}%, ${ltrTheme.spacing(
        (videoScaleWidth + 5) / aspectRatio
      )})`,
    };
  })();

  return (
    <>
      <Box
        sx={{
          ...{
            position: "absolute",
            top: ltrTheme.spacing(2),
            left: ltrTheme.spacing(2),
            zIndex: "modal",
            "> div:first-of-type": {
              position: "absolute",
            },
            width: { xs: "50%", md: "100%" },
          },
          ...myVideoDims,
        }}
      >
        {videoPreview}
      </Box>
      <Grid
        container
        sx={{
          m: 0,
          p: 2,
          height: 1,
          width: 1,
          overflow: "auto",
          backgroundColor: grey[900],
        }}
      >
        {meetingInfo}
      </Grid>
    </>
  );
}

function LaptopDesktopLobby({
  videoPreview,
  meetingInfo,
  controls,
}: LaptopDesktopScreenProps) {
  return (
    <Grid
      container
      spacing={5}
      sx={{
        alignItems: "center",
        justifyContent: "center",
        height: 1,
        width: 1,
        margin: 0,
        pr: { xs: 0, md: 5 },
      }}
    >
      <Grid
        size={{ xs: 7 }}
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          padding: 0,
          maxWidth: "100%",
          maxHeight: "100vh",
          "& > *": {
            // Target the VideoParticipant
            maxWidth: "100%",
            maxHeight: "70vh",
            width: "auto",
            height: "auto",
          },
        }}
      >
        {videoPreview}
        {controls}
      </Grid>
      {meetingInfo}
    </Grid>
  );
}

function PrivacyLevelStatement({
  features,
  callType,
}: {
  features: CallFeature[];
  callType: CallType;
}) {
  const disclaimer = securityDisclaimerCall(callType, features);
  if (!disclaimer) return null;
  if (!isConfidentialCall(features)) {
    return (
      <Typography
        variant="body2"
        sx={{
          color: { xs: common.white, md: "text.secondary" },
        }}
      >
        {disclaimer}
      </Typography>
    );
  }
  return <Alert severity="error">{disclaimer}</Alert>;
}

export default function Lobby({
  currentParticipant,
  call,
  fromPath,
  standalone,
  title,
  onJoinButtonClick,
  beginMuted,
  beginHidden,
  ...rest
}: Props) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const callHasEnded = call.callStatus === CallCallStatus.Ended;
  const isWebinar = callIsWebinar(call);
  const isHost =
    isWebinar && currentParticipant.role === ParticipantRole.WebinarHost;
  const isWebinarAttendee = isWebinar && !isHost;

  const isBelowLaptop = belowLaptop(measureScreenWidth);
  const isMobileOrSmallTablet = belowLargeTablet(measureScreenWidth);

  const userMedia = useLocalVideo();

  const [participantsAccordionExpanded, setParticipantsAccordionExpanded] =
    useState<boolean>(false);

  const handleParticipantsAccordionChange = useCallback(
    (expanded: boolean) => {
      setParticipantsAccordionExpanded(expanded);
    },
    [setParticipantsAccordionExpanded]
  );

  const now = useNow({ updateDelay: 15 * 1000 });
  const isBeforeScheduledStart = now.getTime() < call.startedAt;
  const isAfterScheduledEnd = now.getTime() > call.endedAt;

  const lobbyStatus =
    callHasEnded || isAfterScheduledEnd
      ? "ended"
      : isBeforeScheduledStart
        ? "early"
        : onJoinButtonClick
          ? "ready"
          : "loading";

  useEffect(() => {
    if (lobbyStatus === "ended") {
      sendToParent("CALL_ENDED", { callId: call.id });
    }
  }, [lobbyStatus, call.id]);

  useOnLoad(() => {
    track("Call - Lobby", {
      callType: call.callType,
      lobbyStatus,
    });
  });

  const callTitle = (() => {
    if (title) {
      return title;
    }
    if (isWebinar) {
      // Host will not see the resident's name
      return currentParticipant.role === ParticipantRole.WebinarHost
        ? t("Webinar")
        : // Residents will see the host name
          call.participants.find((r) => r.role === ParticipantRole.WebinarHost)
            ?.fullName || t("Webinar");
    }
    // Non webinars will see the other participants name.
    return call.participants
      .filter((c) => c.externalId !== currentParticipant.externalId)
      .map((c) => c.fullName)
      .join(", ");
  })();

  const meetingInfo = (
    <Grid
      size={{ xs: 12, md: 5 }}
      sx={{
        p: 0,
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: 1,
          justifyContent: { xs: "flex-end", md: "center" },
          paddingBottom: { xs: ltrTheme.spacing(0), md: ltrTheme.spacing(5) },
        }}
      >
        <Typography
          variant="h2"
          component="span"
          sx={{
            mb: 0.5,
            color: { xs: common.white, md: "text.primary" },
          }}
        >
          {callTitle}
        </Typography>
        <Typography
          variant="subtitle1"
          sx={{
            mb: 3,
            color: { xs: common.white, md: "text.secondary" },
          }}
        >
          {formatTimeRange(call.startedAt, call.endedAt)}
        </Typography>
        {isWebinar && (
          <>
            {isHost && (
              <Box
                sx={{
                  maxHeight: "200px",
                  overflow: "auto",
                  mb: 3,
                  pl: 1.5,
                  backgroundColor: "inherit",
                  ...(participantsAccordionExpanded
                    ? {
                        borderBottom: 1,
                        borderBottomColor: isMobileOrSmallTablet
                          ? common.white
                          : "divider",
                      }
                    : {}),
                }}
              >
                <MeetingParticipantsAccordion
                  currentParticipant={currentParticipant}
                  call={call}
                  sx={{
                    color: isMobileOrSmallTablet
                      ? common.white
                      : "text.primary",
                    backgroundColor: "inherit",
                  }}
                  expandIconSx={{
                    color: isMobileOrSmallTablet
                      ? common.white
                      : "action.active",
                  }}
                  textColor={{
                    primary: isMobileOrSmallTablet
                      ? common.white
                      : "text.primary",
                    secondary: isMobileOrSmallTablet
                      ? common.white
                      : "text.secondary",
                  }}
                  onChange={handleParticipantsAccordionChange}
                />
              </Box>
            )}
            <WebinarInfoCard
              isHost={isHost}
              sx={addStylesForDevices(
                isMobileOrSmallTablet,
                { mb: 3 },
                {
                  backgroundColor: "inherit",
                  borderColor: common.white,
                  color: common.white,
                }
              )}
              textColor={isMobileOrSmallTablet ? common.white : "text.primary"}
            />
          </>
        )}
        <Stack
          sx={{
            gap: 3,
          }}
        >
          {lobbyStatus === "ended" ? (
            <Box>
              <Alert severity="error">{t("This call has ended.")}</Alert>
            </Box>
          ) : (
            <>
              <PrivacyLevelStatement
                features={call.features}
                callType={call.callType}
              />
              {lobbyStatus === "early" ? (
                <Box>
                  <Alert severity="warning">
                    {t(
                      "This call has not started yet. You will be able to join during the scheduled time."
                    )}
                  </Alert>
                </Box>
              ) : lobbyStatus === "ready" ? (
                <Button
                  variant="contained"
                  onClick={onJoinButtonClick}
                  sx={addStylesForDevices(isBelowLaptop, {}, { width: 1 })}
                >
                  {isWebinar
                    ? isHost
                      ? t("Start class")
                      : t("Join class")
                    : t("Join call")}
                </Button>
              ) : (
                <Button
                  disabled
                  variant="contained"
                  sx={addStylesForDevices(
                    isMobileOrSmallTablet,
                    {},
                    {
                      width: 1,
                      backgroundColor: `${darkPalette.action.disabled}!important`,
                      color: `${common.white}!important`,
                    }
                  )}
                >
                  {t("Loading")}
                </Button>
              )}
              <Button
                variant="text"
                sx={{
                  color: isMobileOrSmallTablet ? common.white : "primary.main",
                }}
                onClick={() => {
                  const navigateTo = standalone
                    ? "/test-devices"
                    : "/test-lobby";
                  navigate(navigateTo, {
                    state: {
                      fromPath,
                      fromName: t("Call"),
                    },
                  });
                }}
              >
                {t("Test sound and video")}
              </Button>
            </>
          )}
          {isMobileOrSmallTablet && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
              }}
            >
              <Stack
                direction="row"
                sx={{
                  gap: 1,
                  background: (th) => alpha(th.palette.background.default, 0.8),
                  borderRadius: "10000px",
                  px: 1,
                  py: 1.5,
                  backdropFilter: "blur(5px)",
                  height: ltrTheme.spacing(8),
                  whiteSpace: "nowrap",
                }}
              >
                <MediaControls
                  micDisabled={isWebinarAttendee}
                  micDisabledHelperText={t("Only hosts can unmute you")}
                  hidden={beginHidden}
                  muted={beginMuted}
                  {...rest}
                  onToggleVideo={
                    isWebinarAttendee ? undefined : rest.onToggleVideo
                  }
                />
                <Button
                  onClick={() => {
                    sendToParent("USER_LEFT", {
                      callId: call.id,
                      userId: currentParticipant.externalId,
                    });
                    navigate("/");
                  }}
                  sx={{
                    whiteSpace: "nowrap",
                    backgroundColor: red[700],
                    borderRadius: "10000px",
                    mx: 1,
                    color: common.white,
                    "&:hover": {
                      backgroundColor: lightPalette.error.main,
                    },
                  }}
                >
                  {t("Leave")}
                </Button>
              </Stack>
            </Box>
          )}
        </Stack>
      </Box>
    </Grid>
  );

  const videoPreview = userMedia ? (
    <VideoParticipant
      user={currentParticipant}
      audioStream={undefined}
      stream={userMedia}
      audioMuted={beginMuted}
      videoPaused={beginHidden}
      origin="local"
      mirrored
      containerSx={{ borderRadius: 4 }}
      transitionDuration={20} // Small delay to allow for video to load before transforming
    />
  ) : undefined;

  if (isMobileOrSmallTablet) {
    return (
      <MobileTabletLobby
        meetingInfo={meetingInfo}
        videoPreview={videoPreview}
        stream={userMedia}
      />
    );
  }

  return (
    <LaptopDesktopLobby
      meetingInfo={meetingInfo}
      videoPreview={videoPreview}
      controls={
        <Box
          sx={{
            py: { xs: 3, md: 2 },
            "> span": {
              my: 0,
              mx: 1,
            },
          }}
        >
          <MediaControls
            micDisabled={isWebinarAttendee}
            micDisabledHelperText={t("Only hosts can unmute you")}
            hidden={beginHidden}
            muted={beginMuted}
            {...rest}
            onToggleVideo={isWebinarAttendee ? undefined : rest.onToggleVideo}
          />
          <Button
            onClick={() => navigate("/")}
            sx={{
              whiteSpace: "nowrap",
              backgroundColor: red[700],
              borderRadius: "10000px",
              mx: 1,
              color: common.white,
              "&:hover": {
                backgroundColor: lightPalette.error.main,
              },
            }}
          >
            {t("Leave")}
          </Button>
        </Box>
      }
    />
  );
}
